// ============================================================
// Intigo HTTP client — talks to the Cloudflare Pages Function
// proxy at /api/delivery/intigo/<path>. The proxy injects the
// X-API-Key from the tenant DB; we forward the user's Supabase
// JWT so the proxy can authorize.
// ============================================================

export interface IntigoClientOptions {
  /** Override the proxy base URL (default: same-origin /api/delivery/intigo). */
  proxyBaseUrl?: string;
  /** Returns the current Supabase access token, or null if signed out. */
  getAccessToken: () => Promise<string | null> | string | null;
  /** Optional Supabase project URL forwarded to the proxy as a header so the
   *  proxy can locate the tenant DB without server-side env config. */
  supabaseUrl?: string | null;
  /** Optional Supabase anon key forwarded as a header. Public by design. */
  supabaseAnonKey?: string | null;
}

export class IntigoClient {
  private readonly base: string;
  private readonly getAccessToken: IntigoClientOptions['getAccessToken'];
  private readonly supabaseUrl: string | null;
  private readonly supabaseAnonKey: string | null;

  constructor(options: IntigoClientOptions) {
    this.base = options.proxyBaseUrl ?? '/api/delivery/intigo';
    this.getAccessToken = options.getAccessToken;
    this.supabaseUrl = options.supabaseUrl ?? null;
    this.supabaseAnonKey = options.supabaseAnonKey ?? null;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }
  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }
  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await Promise.resolve(this.getAccessToken());
    if (!token) throw new Error('not_authenticated');

    const url = `${this.base}/${path.replace(/^\//, '')}`;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (this.supabaseUrl) headers['X-Supabase-URL'] = this.supabaseUrl;
    if (this.supabaseAnonKey) headers['X-Supabase-AnonKey'] = this.supabaseAnonKey;
    const res = await fetch(url, {
      method,
      headers,
      body: body == null ? undefined : JSON.stringify(body),
    });

    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }

    if (!res.ok) {
      const errBody = parsed as { detail?: unknown; error?: unknown } | null;
      const detail = errBody?.detail ?? errBody?.error ?? text;
      const err = new Error(
        `intigo_${res.status}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`,
      );
      (err as Error & { status?: number; body?: unknown }).status = res.status;
      (err as Error & { status?: number; body?: unknown }).body = parsed;
      throw err;
    }

    return parsed as T;
  }
}
